import { useState, useMemo } from 'react';
import {
  Container,
  Stack,
  Button,
  Text,
  Group,
  Table,
  Checkbox,
  Alert,
  Select,
  Paper,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconPlus, IconAlertCircle } from '@tabler/icons-react';
import { useNotes, useBulkDeleteNotes, useBulkArchiveNotes } from '../api/notes';
import { PageHeader } from '../components/common/PageHeader';
import './NotesPage.css';

interface NotesPageProps {
  onEditNote: (noteId: string) => void;
  onCreateNote: () => void;
}

export default function NotesPage({ onEditNote, onCreateNote }: NotesPageProps) {
  const { data: notes = [], isLoading } = useNotes();
  const bulkDeleteMutation = useBulkDeleteNotes();
  const bulkArchiveMutation = useBulkArchiveNotes();
  const isDesktop = useMediaQuery('(min-width: 1408px)');

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filter notes
  const filteredNotes = useMemo(() => {
    return notes.filter((note) => {
      if (statusFilter && note.status !== statusFilter) return false;
      if (categoryFilter && note.category !== categoryFilter) return false;
      return true;
    });
  }, [notes, statusFilter, categoryFilter]);

  const handleSelectNote = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredNotes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredNotes.map((n) => n.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    try {
      await bulkDeleteMutation.mutateAsync(Array.from(selectedIds));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error bulk deleting notes:', error);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;

    try {
      await bulkArchiveMutation.mutateAsync(Array.from(selectedIds));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error bulk archiving notes:', error);
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <PageHeader
          title="Notes"
          subtitle="Create and manage markdown notes."
          actions={
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => onCreateNote()}
              variant="filled"
            >
              Create Note
            </Button>
          }
        />

        {isDesktop ? (
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Group>
                <Select
                  label="Status"
                  placeholder="All statuses"
                  data={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'archived', label: 'Archived' },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  clearable
                  w={150}
                />
                <Select
                  label="Category"
                  placeholder="All categories"
                  data={[
                    { value: 'uncategorized', label: 'Uncategorized' },
                    { value: 'job', label: 'Job' },
                  ]}
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  clearable
                  w={150}
                />
              </Group>

              {/* Bulk Actions Alert */}
              {selectedIds.size > 0 && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="blue"
                  title={`${selectedIds.size} note${selectedIds.size !== 1 ? 's' : ''} selected`}
                >
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      loading={bulkArchiveMutation.isPending}
                      onClick={handleBulkArchive}
                    >
                      Archive
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      loading={bulkDeleteMutation.isPending}
                      onClick={handleBulkDelete}
                    >
                      Delete
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Cancel
                    </Button>
                  </Group>
                </Alert>
              )}

              {/* Notes Table */}
              {filteredNotes.length === 0 ? (
                <Text c="dimmed" ta="center" py="lg">
                  {notes.length === 0
                    ? 'No notes yet. Create one to get started.'
                    : 'No notes match the selected filters.'}
                </Text>
              ) : (
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '40px' }}>
                        <Checkbox
                          checked={selectedIds.size === filteredNotes.length && filteredNotes.length > 0}
                          indeterminate={selectedIds.size > 0 && selectedIds.size < filteredNotes.length}
                          onChange={handleSelectAll}
                        />
                      </Table.Th>
                      <Table.Th>Title</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Status</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Category</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Created</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredNotes.map((note) => (
                      <Table.Tr key={note.id}>
                        <Table.Td>
                          <Checkbox
                            checked={selectedIds.has(note.id)}
                            onChange={() => handleSelectNote(note.id)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text
                            style={{ cursor: 'pointer', color: 'var(--mantine-color-blue-6)' }}
                            onClick={() => onEditNote(note.id)}
                          >
                            {note.title}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" tt="capitalize">
                            {note.status}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" tt="capitalize">
                            {note.category}
                          </Text>
                        </Table.Td>
                        <Table.Td>{formatDate(note.created_at)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        ) : (
          <>
            <Group>
              <Select
                label="Status"
                placeholder="All statuses"
                data={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'archived', label: 'Archived' },
                ]}
                value={statusFilter}
                onChange={setStatusFilter}
                clearable
                w={150}
              />
              <Select
                label="Category"
                placeholder="All categories"
                data={[
                  { value: 'uncategorized', label: 'Uncategorized' },
                  { value: 'job', label: 'Job' },
                ]}
                value={categoryFilter}
                onChange={setCategoryFilter}
                clearable
                w={150}
              />
            </Group>

            {/* Bulk Actions Alert */}
            {selectedIds.size > 0 && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="blue"
                title={`${selectedIds.size} note${selectedIds.size !== 1 ? 's' : ''} selected`}
              >
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="default"
                    loading={bulkArchiveMutation.isPending}
                    onClick={handleBulkArchive}
                  >
                    Archive
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    loading={bulkDeleteMutation.isPending}
                    onClick={handleBulkDelete}
                  >
                    Delete
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Cancel
                  </Button>
                </Group>
              </Alert>
            )}

            {/* Notes Table */}
            {filteredNotes.length === 0 ? (
              <Text c="dimmed" ta="center" py="lg">
                {notes.length === 0
                  ? 'No notes yet. Create one to get started.'
                  : 'No notes match the selected filters.'}
              </Text>
            ) : (
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: '40px' }}>
                      <Checkbox
                        checked={selectedIds.size === filteredNotes.length && filteredNotes.length > 0}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < filteredNotes.length}
                        onChange={handleSelectAll}
                      />
                    </Table.Th>
                    <Table.Th>Title</Table.Th>
                    <Table.Th style={{ width: '100px' }}>Status</Table.Th>
                    <Table.Th style={{ width: '120px' }}>Category</Table.Th>
                    <Table.Th style={{ width: '120px' }}>Created</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredNotes.map((note) => (
                    <Table.Tr key={note.id}>
                      <Table.Td>
                        <Checkbox
                          checked={selectedIds.has(note.id)}
                          onChange={() => handleSelectNote(note.id)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text
                          style={{ cursor: 'pointer', color: 'var(--mantine-color-blue-6)' }}
                          onClick={() => onEditNote(note.id)}
                        >
                          {note.title}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" tt="capitalize">
                          {note.status}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" tt="capitalize">
                          {note.category}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatDate(note.created_at)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}
